'use client'

import { useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'

// --- Types ---
interface ServiceState { status?: string; latencyMs?: number; detail?: string }
interface HealthData {
  status: 'ok' | 'degraded'
  services: Record<string, ServiceState>
  version: string
  uptime?: number
}
interface GpuNode { id: string; status: string; live: boolean }

// System Status Bar — footer passif, jamais un 2e dashboard. Détail des
// métriques (GPU/VRAM/Workers, agents actifs, missions) vit désormais dans
// les panneaux dédiés (GPU Mesh, Agent Observatory, Mission Journal) — le
// footer n'affiche que la santé globale, jamais une donnée fabriquée
// (voir modules/agents/health-score.ts : pas de score système agrégé
// aujourd'hui, seulement un ratio réel services ok/total).
export default function Footer() {
  const { data: session } = useSession()
  const [health, setHealth] = useState<HealthData | null>(null)
  const [gpuOnline, setGpuOnline] = useState<number | null>(null)
  const [gpuTotal, setGpuTotal] = useState<number | null>(null)

  // Santé services réels — DB, Redis, Qdrant, Ollama, SearXNG, MinIO.
  useEffect(() => {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL ?? ''
    const check = () =>
      fetch(`${apiUrl}/health`)
        .then(r => r.json())
        .then(setHealth)
        .catch(() => null)
    check()
    const iv = setInterval(check, 30_000)
    return () => clearInterval(iv)
  }, [])

  // GPU Mesh — nombre de nœuds en ligne, détail (VRAM/util) dans le panneau GPU Mesh.
  useEffect(() => {
    if (!session) return
    const check = () =>
      fetch('/api/gpu-nodes')
        .then(r => (r.ok ? r.json() : null))
        .then((data: { nodes?: GpuNode[] } | null) => {
          const nodes = Array.isArray(data?.nodes) ? data.nodes : []
          setGpuOnline(nodes.filter(n => n.status === 'online' || n.live).length)
          setGpuTotal(nodes.length)
        })
        .catch(() => null)
    check()
    const iv = setInterval(check, 30_000)
    return () => clearInterval(iv)
  }, [session])

  const online = health?.status === 'ok'
  const services = health ? Object.values(health.services) : []
  const servicesOk = services.filter(s => s.status === 'ok').length
  const uptime = health?.uptime
    ? `${Math.floor(health.uptime / 3600)}h${Math.floor((health.uptime % 3600) / 60)}m`
    : null
  const statusColor = online ? 'var(--teal)' : 'var(--amber)'

  return (
    <footer
      style={{
        position:   'relative',
        width:      '100%',
        height:     '100%',
        maxHeight:  36,
        background: 'var(--bg-deep)',
        borderTop:  '1px solid rgba(79,124,255,0.15)',
        overflow:   'hidden',
        fontFamily: 'var(--font-mono)',
      }}
    >
      {/* Courbe — décorative, superposée, n'affecte pas la hauteur (36px, contrat AppShell) */}
      <svg
        aria-hidden
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        viewBox="0 0 100 36"
        preserveAspectRatio="none"
      >
        <path d="M0 10 Q50 -4 100 10 L100 36 L0 36 Z" fill="rgba(79,124,255,0.03)" />
      </svg>

      <div
        style={{
          position:  'relative',
          zIndex:    1,
          height:    '100%',
          display:   'flex',
          alignItems: 'center',
          padding:   '0 16px',
          gap:       14,
          fontSize:  10,
          color:     'rgba(255,255,255,0.4)',
          overflowX: 'auto',
          overflowY: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        {/* État système */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <span
            style={{
              width: 6, height: 6, borderRadius: '50%',
              background: statusColor,
              boxShadow:  `0 0 8px ${statusColor}`,
            }}
          />
          <span style={{ color: statusColor, letterSpacing: '0.04em', fontWeight: 600 }}>
            {online ? 'CORE ONLINE' : 'CORE DEGRADED'}
          </span>
          {health && <span style={{ opacity: 0.5 }}>· {servicesOk}/{services.length}</span>}
        </div>

        {gpuTotal !== null && (
          <>
            <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.06)', flexShrink: 0 }} />
            <span style={{ flexShrink: 0, opacity: 0.6 }}>
              GPU Mesh <span style={{ color: 'var(--accent-cyan)' }}>{gpuOnline}/{gpuTotal}</span>
            </span>
          </>
        )}

        {/* Centre — identité, discrète */}
        <div
          style={{
            position:  'absolute',
            left:      '50%',
            transform: 'translateX(-50%)',
            opacity:   0.18,
            letterSpacing: '0.25em',
            fontSize:  9,
            animation: 'footerPulse 6s ease-in-out infinite',
            pointerEvents: 'none',
          }}
        >
          AMIRAL BLANC
        </div>

        {/* Droite */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          {uptime && <span style={{ opacity: 0.35 }}>{uptime}</span>}
          <span style={{ opacity: 0.3 }}>v{health?.version ?? '—'}</span>
          <span style={{ opacity: 0.35, letterSpacing: '0.06em' }}>AIDE</span>
        </div>
      </div>

      <style>{`
        @keyframes footerPulse {
          0%, 100% { opacity: 0.12; }
          50%      { opacity: 0.28; }
        }
      `}</style>
    </footer>
  )
}
